import React from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  InputAdornment
} from "@mui/material";
import {
  BsCashCoin,
  BsCameraVideo,
  BsDatabase,
  BsCheckCircle,
  BsCalendarEvent,
  BsLayers
} from "react-icons/bs";
import RFG from "../../libs/inputs/RFG";

const CURRENCY_OPTIONS = [
  { code: "USD", symbol: "$", label: "USD ($) - US Dollar" },
  { code: "EUR", symbol: "€", label: "EUR (€) - Euro" },
  { code: "GBP", symbol: "£", label: "GBP (£) - British Pound" },
  { code: "INR", symbol: "₹", label: "INR (₹) - Indian Rupee" },
  { code: "AED", symbol: "د.إ", label: "AED (د.إ) - UAE Dirham" },
  { code: "SAR", symbol: "﷼", label: "SAR (﷼) - Saudi Riyal" },
  { code: "QAR", symbol: "﷼", label: "QAR (﷼) - Qatari Riyal" },
  { code: "KWD", symbol: "KD", label: "KWD (KD) - Kuwaiti Dinar" },
  { code: "CAD", symbol: "C$", label: "CAD (C$) - Canadian Dollar" },
  { code: "AUD", symbol: "A$", label: "AUD (A$) - Australian Dollar" },
  { code: "SGD", symbol: "S$", label: "SGD (S$) - Singapore Dollar" },
];

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: "14px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  outline: "none",
  backgroundColor: "#fff",
  transition: "border-color 0.2s, box-shadow 0.2s",
  color: "#1e293b",
  boxSizing: "border-box"
};

const sectionCardStyle = {
  p: 2.5,
  mb: 2.5,
  borderRadius: 2.5,
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  backgroundColor: "#ffffff"
};

export default function Form({
  title,
  companies = [],
  projects = [],
  users = [],
  defaultValues = {},
  onSubmit = () => { },
  onChange = () => { },
  onCancel,
}) {
  const initialValues = {
    currency: "USD",
    days: 30,
    image_retention: 30,
    video_retention: 30,
    hybrid: 0,
    hd_ptz: 0,
    _4k_ptz: 0,
    _24mp: 0,
    _32mp: 0,
    _50mp: 0,
    _60mp: 0,
    ...defaultValues,
  };

  const { handleSubmit, control, reset } = useForm({
    defaultValues: initialValues,
    mode: "all"
  });

  React.useEffect(() => {
    reset({
      currency: "USD",
      days: 30,
      image_retention: 30,
      video_retention: 30,
      hybrid: 0,
      hd_ptz: 0,
      _4k_ptz: 0,
      _24mp: 0,
      _32mp: 0,
      _50mp: 0,
      _60mp: 0,
      ...defaultValues,
    });
  }, [defaultValues, reset]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 900, mx: "auto" }}>
      <form
        onSubmit={handleSubmit((data) => {
          console.log("Package submit data:", data);
          onSubmit(data);
        })}
      >
        {/* Section 1: Plan & Pricing */}
        <Paper sx={sectionCardStyle} elevation={0}>
          <Box display="flex" alignItems="center" gap={1.2} mb={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "#e0f2fe",
                color: "#0284c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16
              }}
            >
              <BsCashCoin />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                Plan & Pricing
              </Typography>
              <Typography variant="caption" color="#64748b">
                Define the package title, billing currency, amount, and subscription period
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.8, borderColor: "#f1f5f9" }} />

          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12} sm={6}>
              <RFG
                name="title"
                label="Package Title *"
                control={control}
                rules={{ required: true, maxLength: 200 }}
              >
                <input
                  style={inputStyle}
                  placeholder="e.g. Starter, Premium, Enterprise"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* Currency */}
            <Grid item xs={12} sm={3}>
              <RFG
                name="currency"
                label="Currency *"
                control={control}
                rules={{ required: true }}
              >
                <select style={{ ...inputStyle, cursor: "pointer" }}>
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </RFG>
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={3}>
              <RFG
                name="amount"
                label="Amount *"
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: /^[0-9]+(\.[0-9]+)?$/,
                    message: "Valid number only"
                  }
                }}
              >
                <input
                  style={inputStyle}
                  placeholder="0.00"
                  type="number"
                  step="any"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* Days (Duration) */}
            <Grid item xs={12} sm={6}>
              <RFG
                name="days"
                label="Billing Cycle (Days) *"
                control={control}
                rules={{
                  required: true,
                  pattern: { value: /^[0-9]+$/, message: "Numbers only" }
                }}
              >
                <input
                  style={inputStyle}
                  placeholder="e.g. 30"
                  type="number"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* Validity Date */}
            <Grid item xs={12} sm={6}>
              <RFG
                name="validity_date"
                label="Validity Expiration Date"
                control={control}
                rules={{ required: false }}
              >
                <input
                  style={inputStyle}
                  type="date"
                  autoComplete="off"
                />
              </RFG>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: -0.5 }}>
                Account access expires at night 12 (midnight) on this date
              </Typography>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <RFG
                name="description"
                label="Package Description"
                control={control}
                rules={{ maxLength: 1000 }}
              >
                <textarea
                  style={{
                    ...inputStyle,
                    minHeight: "70px",
                    resize: "vertical",
                    fontFamily: "inherit"
                  }}
                  placeholder="Brief overview of features, inclusions, and terms for this package..."
                  rows={2}
                  autoComplete="off"
                />
              </RFG>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 2: Storage & Media Retention */}
        <Paper sx={sectionCardStyle} elevation={0}>
          <Box display="flex" alignItems="center" gap={1.2} mb={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "#fef3c7",
                color: "#d97706",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16
              }}
            >
              <BsDatabase />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                Media Retention & Cloud Storage
              </Typography>
              <Typography variant="caption" color="#64748b">
                Set how long snapshot images and timelapse videos are stored in the cloud
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.8, borderColor: "#f1f5f9" }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <RFG
                name="image_retention"
                label="Image Retention (Days) *"
                control={control}
                rules={{
                  required: true,
                  pattern: { value: /^[0-9]+$/, message: "Numbers only" }
                }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="30"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RFG
                name="video_retention"
                label="Video Retention (Days) *"
                control={control}
                rules={{
                  required: true,
                  pattern: { value: /^[0-9]+$/, message: "Numbers only" }
                }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="30"
                  autoComplete="off"
                />
              </RFG>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 3: Camera Allowances & Resolution Limits */}
        <Paper sx={sectionCardStyle} elevation={0}>
          <Box display="flex" alignItems="center" gap={1.2} mb={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "#f3e8ff",
                color: "#7e22ce",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16
              }}
            >
              <BsCameraVideo />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                Camera Allowances & Limits
              </Typography>
              <Typography variant="caption" color="#64748b">
                Maximum camera counts and resolution tiers allowed under this package
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.8, borderColor: "#f1f5f9" }} />

          <Grid container spacing={2}>
            {/* AI Camera */}
            <Grid item xs={12} sm={4}>
              <RFG
                name="hybrid"
                label="AI Cameras (Quota)"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* HD PTZ */}
            <Grid item xs={12} sm={4}>
              <RFG
                name="hd_ptz"
                label="HD PTZ Cameras"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* 4K PTZ */}
            <Grid item xs={12} sm={4}>
              <RFG
                name="_4k_ptz"
                label="4K PTZ Cameras"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* 24 MP */}
            <Grid item xs={6} sm={3}>
              <RFG
                name="_24mp"
                label="24 MP Cameras"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* 32 MP */}
            <Grid item xs={6} sm={3}>
              <RFG
                name="_32mp"
                label="32 MP Cameras"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* 50 MP */}
            <Grid item xs={6} sm={3}>
              <RFG
                name="_50mp"
                label="50 MP Cameras"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>

            {/* 60 MP */}
            <Grid item xs={6} sm={3}>
              <RFG
                name="_60mp"
                label="60 MP Cameras"
                control={control}
                rules={{ pattern: { value: /^[0-9]+$/, message: "Numbers only" } }}
              >
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="0"
                  autoComplete="off"
                />
              </RFG>
            </Grid>
          </Grid>
        </Paper>

        {/* Submit Actions */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          pt={1}
        >
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "#64748b",
                borderColor: "#cbd5e1",
                px: 3,
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" }
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            type="submit"
            variant="contained"
            className="btn-primary"
            startIcon={<BsCheckCircle />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3.5,
              py: 1,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
            }}
          >
            Save Package
          </Button>
        </Box>
      </form>
    </Box>
  );
}
